import { Ticket, TicketPlus, Users } from "lucide-react";

export interface NyeremenyjatekProp {
  id: string;
  title: string;
  description?: string;
  image?: string;
  site?: string;
  keyword: string;
  timer?: Date;
  status: number;
  ticketPrice: number;
  maxTickets: number;
  maxTicketsPerUser: number;
  createdAt: Date;
  endedAt?: Date;
  tickets: NyeremenyjatekTickets[];
  winners: NyeremenyjatekWinner[];
  ticketsCount: number;
}

export interface Winner {
  name: string;
  twitch_id: number;
  position: number;
  revealed: boolean;
  verified: boolean;
}

export interface WinnerSlotProps {
  winner: Winner;
  isRolling: boolean;
  allParticipants: string[];
}

export interface NyeremenyjatekTickets {
  nyeremenyjatekId: string;
  discord_id?: string;
  tickets: number;
  twitch_id: number;
  twitch_name: string;
  joinedAt: Date;
}

export interface NyeremenyjatekWinner {
  id: number;
  nyeremenyjatekId: string;
  discord_id?: string;
  twitch_id: number;
  twitch_name: string;
  tickets: number;
  selectedAt: Date;
}

export interface NyeremenyjatekDisplayProps {
  data: NyeremenyjatekProp;
}

export function NyeremenyjatekDisplay({
  data,
}: Readonly<NyeremenyjatekDisplayProps>) {
  const totalUsers = data.tickets?.length ?? 0;
  const totalTickets = data.ticketsCount ?? 0;

  return (
    <div className="nyeremenyjatek-container rounded-3xl">
      <div className="animate-[scale_700ms_ease-in-out] w-full h-full bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-950 rounded-2xl p-6 shadow-2xl border border-zinc-800/50 backdrop-blur-sm flex flex-col justify-evenly">
        <div className="text-center mb-5">
          <div className="inline-flex items-center gap-2 mb-2">
            <h2 className="text-2xl font-bold text-white tracking-tight">
              {data.title}
            </h2>
          </div>
          {data.description && (
            <p className="text-sm py-2 text-zinc-400 font-medium">
              {data.description}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2.5 mb-4">
          <div className="bg-zinc-800/40 backdrop-blur-sm rounded-xl p-3 border border-zinc-700/50 hover:border-zinc-600/50 transition-colors">
            <div className="flex items-center gap-2.5 ">
              <div className="p-1.5 bg-blue-500/10 rounded-lg">
                <Users className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <p className="text-[10px] text-zinc-500 font-medium uppercase tracking-wide">
                  Users
                </p>
                <p className="text-xl font-bold text-white">{totalUsers}</p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-800/40 backdrop-blur-sm rounded-xl p-3 border border-zinc-700/50 hover:border-zinc-600/50 transition-colors">
            <div className="flex items-center gap-2.5 ">
              <div className="p-1.5 bg-purple-500/10 rounded-lg">
                <Ticket className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <p className="text-[10px] text-zinc-500 font-medium uppercase tracking-wide">
                  Tickets
                </p>
                <p className="text-xl font-bold text-white">{totalTickets}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2.5 mb-4">
          <div className="bg-zinc-800/30 rounded-xl p-3 border border-zinc-700/30 text-center ">
            <div className="flex justify-center mb-1.5">
              <div className="p-1">
                <img
                  alt="Ticket Price"
                  src="https://neverdoit.com/images/fullsize.png"
                  className="w-6 h-6  object-contain"
                />
              </div>
            </div>
            <p className="text-[10px] text-zinc-500 mb-0.5 font-medium uppercase tracking-wide">
              Ticket Price
            </p>
            <p className="text-base text-white font-bold">{data.ticketPrice}</p>
          </div>

          <div className="bg-zinc-800/30 rounded-xl p-3 border border-zinc-700/30 text-center ">
            <div className="flex justify-center mb-1.5">
              <div className="p-1.5 bg-blue-500/10 rounded-lg">
                <Ticket className="w-4 h-4 text-blue-400" />
              </div>
            </div>
            <p className="text-[10px] text-zinc-500 mb-0.5 font-medium uppercase tracking-wide">
              Max Tickets / User
            </p>
            <p className="text-base text-white font-bold">
              {data.maxTicketsPerUser}
            </p>
          </div>

          <div className="bg-zinc-800/30 rounded-xl p-3 border border-zinc-700/30 text-center ">
            <div className="flex justify-center mb-1.5">
              <div className="p-1.5 bg-purple-500/10 rounded-lg">
                <Ticket className="w-4 h-4 text-purple-400" />
              </div>
            </div>
            <p className="text-[10px] text-zinc-500 mb-0.5 font-medium uppercase tracking-wide">
              Max Tickets
            </p>
            <p className="text-base text-white font-bold">{data.maxTickets}</p>
          </div>
        </div>

        <div className="flex justify-center mb-5">
          <div className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl w-full py-4 backdrop-blur-sm ">
            <TicketPlus className="w-15 h-15 text-purple-300" />
            <span className="text-purple-100  font-semibold tracking-wide">
              {data.keyword} [tickets]
            </span>
          </div>
        </div>

        {data.image && (
          <div className="flex justify-center">
            <div className="inline-flex items-center justify-center backdrop-blur-sm h-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl w-full py-2 ">
              <img
                src={data.image || "/placeholder.svg"}
                alt={data.site ?? "Giveaway"}
                className="h-full w-auto object-contain"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
